import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef, useState } from 'react';

import { createLoginOptions } from '@/config/auth0';

export function usePermissions() {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } =
    useAuth0();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const loginRedirectStarted = useRef(false);

  useEffect(() => {
    const getPermissions = async () => {
      if (!isAuthenticated) {
        setPermissions([]);
        setLoading(false);
        return;
      }

      try {
        const token = await getAccessTokenSilently();

        // Decode the JWT token to get permissions
        const payload = JSON.parse(atob(token.split('.')[1]));
        // console.log("Access token payload:", payload);

        const tokenPermissions =
          payload.permissions ||
          payload['https://littlebird.co/permissions'] ||
          payload.scope?.split(' ') ||
          [];

        setPermissions(tokenPermissions);
      } catch (error) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'error' in error &&
          error.error === 'invalid_grant'
        ) {
          if (!loginRedirectStarted.current) {
            loginRedirectStarted.current = true;
            await loginWithRedirect(createLoginOptions());
          }
          return;
        }

        console.error('Error getting permissions:', error);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    getPermissions();
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (perms: string[]): boolean => {
    return perms.some((permission) => hasPermission(permission));
  };

  const hasAllPermissions = (perms: string[]): boolean => {
    // console.log("Checking all permissions:", perms, "User has:", permissions);
    return perms.every((permission) => hasPermission(permission));
  };

  const isAdmin = (): boolean => {
    if (import.meta.env.DEV) return true;
    return hasAllPermissions(['read:admin', 'manage:feedback']);
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    permissions,
    loading,
  };
}
